export const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export const formatNumber = (number, decimals = 2) => {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true
  }).format(number);
};

export const formatDate = (date) => {
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

// Normalize profile type: '6S' (6 STADIO) maps to '6' for all config lookups
export const normalizeProfileType = (profileType) => {
  if (profileType === '6S') return '6';
  return profileType;
};

export const formatProductCode = ({
  profileType,
  hasCarter,
  carterCode,
  glassSize,
  finishY,
  finishC,
  isScale,
  fixing,
  length,
  isCustomLength
}) => {
  // Normalize profile type (6S -> 6 for product code generation)
  const normalizedProfile = normalizeProfileType(profileType);

  // Validate and provide default values for missing fields
  const validatedGlassSize = glassSize || getValidGlassSizes(normalizedProfile)[0] || '16';
  const validatedFinishY = finishY || 'G';
  const validatedFinishC = finishC || 'G';

  // Base format: N{NUMERO}-{X}{Y}-{K}{A}{B}{C}
  // Use original profileType for code prefix (e.g., '6S' -> 'N6S', '6' -> 'N6')
  let code = `N${profileType}`;

  // Handle N50 special case (N50 doesn't exist in rules, but keeping for compatibility)
  if (normalizedProfile === '50') {
    let n50Section = '';
    // K: lunghezza - SE 3 o 6 METRI K = NIENTE // SE SU MISURA K = T
    if (isCustomLength && length !== 3000 && length !== 6000) {
      n50Section += 'T';
    }
    // A: fissaggio - SE NON C'È, VA ELIMINATA LA POSIZIONE (A)
    if (fixing) {
      n50Section += fixing;
    }
    // B: sempre 16 per N50
    n50Section += '16';
    // C: finitura - N50 non ha carter, quindi mantieni V1/V2 completo
    n50Section += validatedFinishC;
    return `${code}-${n50Section}`;
  }

  // Add carter section if present (XY)
  // SE IL CARTER NON C'è ELIMINO (-XY)
  if (hasCarter && carterCode) {
    // IMPORTANTE: Y = CARTER
    // Per N116 con carter 1/1S/1C: Se profilo è V1/V2 E carter è V1/V2, rimuovere numero dal carter
    // Per N56 con carter 1/1I: Se finitura è V1/V2, rimuovere numero dal carter (Y=V, C=V1/V2)
    let finishForY = validatedFinishY;
    if (normalizedProfile === '116' && ['1', '1S', '1C'].includes(carterCode)) {
      if ((validatedFinishC === 'V1' || validatedFinishC === 'V2') &&
          (validatedFinishY === 'V1' || validatedFinishY === 'V2')) {
        finishForY = 'V'; // Entrambi V1/V2 → rimuovi numero dal carter
      }
      // Altrimenti mantieni V1/V2 completo
    } else if (normalizedProfile === '56' && ['1', '1I'].includes(carterCode)) {
      // Per N56: Se finitura è V1/V2, nel carter (Y) usa solo V, il numero va in C
      if (validatedFinishY === 'V1' || validatedFinishY === 'V2') {
        finishForY = 'V';
      }
    } else if (normalizedProfile === '6' && carterCode === '1') {
      // Per N6/N6 STADIO: Se finitura è V1/V2, nel carter (Y) usa solo V, il numero va in C
      if (validatedFinishY === 'V1' || validatedFinishY === 'V2') {
        finishForY = 'V';
      }
    } else if (normalizedProfile === '106' && carterCode === '1') {
      // Per N106: Se finitura è V1/V2, nel carter (Y) usa solo V, il numero va in C
      if (validatedFinishY === 'V1' || validatedFinishY === 'V2') {
        finishForY = 'V';
      }
    }
    code += `-${carterCode}${finishForY}`;
  }

  // Build final section in correct order: KABC
  let finalSection = '';

  // K: lunghezza - SE 3 o 6 METRI K = NIENTE // SE SU MISURA K = T
  if (isCustomLength && length !== 3000 && length !== 6000) {
    finalSection += 'T';
  }

  // A: fissaggio - SE NON C'È, VA ELIMINATA LA POSIZIONE (A)
  if (fixing) {
    finalSection += fixing;
  }

  // B: vetro
  finalSection += validatedGlassSize;

  // C: finitura - LOGICA COMPLESSA
  // IMPORTANTE: Nel codice prodotto RAD-XY-KABC
  // Y = finitura CARTER (posizione centrale in XY)
  // C = finitura PROFILO (ultima posizione in KABC)
  // NOTA: Per V1 e V2, rimuovere il numero SOLO quando c'è il carter

  let finishForC = validatedFinishC;

  if (normalizedProfile === '176') {
    // PER 176 CON CARTER: C = G (profilo sempre Grezzo) E Y = COLORE del carter
    // PER 176 SENZA CARTER: C = colore selezionato dall'utente
    if (hasCarter) {
      finishForC = 'G';
    } else {
      finishForC = validatedFinishC;
    }
  } else if (normalizedProfile === '186') {
    // PER 186:
    // SE LINEARE con carter 1/1I: C è sempre G (profilo Grezzo), Y è colore carter
    // SE LINEARE con carter 2: C è colore profilo, Y è colore carter
    // SE IN SCALA: Y E C = COLORE (stesso)
    if (isScale) {
      finishForC = validatedFinishC; // In scala: C = Y (stesso colore)
    } else {
      // Lineare: C = colore profilo (finishC dalla selezione utente)
      finishForC = validatedFinishC;
    }
  } else if (normalizedProfile === '116') {
    // PER 116: Y = finitura CARTER, C = finitura PROFILO
    // SE X = 1 O 1S - può avere colori diversi
    // SE X = 2S - stesso colore
    finishForC = validatedFinishC; // C = profilo
  } else if (['106', '56', '6'].includes(normalizedProfile)) {
    // PER 106, 56, 6 (e 6 STADIO): Y E C SONO STESSO VALORE
    finishForC = validatedFinishC;
  } else {
    // Default
    finishForC = validatedFinishC;
  }

  // IMPORTANTE: Rimuovere il numero da V1/V2 SOLO quando c'è il carter
  // Se NON c'è carter, mantenere V1 o V2 completo
  // ECCEZIONE per N116 con carter 1/1S/1C: Se entrambi (Y e C) sono V1/V2, mantenere V1/V2 in C
  // ECCEZIONE per N56 con carter 1/1I: Mantenere V1/V2 in C (il numero va in C, non in Y)
  // ECCEZIONE per N6 con carter 1: Mantenere V1/V2 in C (il numero va in C, non in Y)
  // ECCEZIONE per N106 con carter 1: Mantenere V1/V2 in C (il numero va in C, non in Y)
  if (hasCarter && (finishForC === 'V1' || finishForC === 'V2')) {
    // Per N116 con carter 1/1S/1C: Se entrambi sono V1/V2, mantieni il numero in C
    if (normalizedProfile === '116' && ['1', '1S', '1C'].includes(carterCode) &&
        (validatedFinishY === 'V1' || validatedFinishY === 'V2')) {
      // Mantieni V1 o V2 completo in C (profilo)
      finishForC = validatedFinishC;
    } else if (normalizedProfile === '56' && ['1', '1I'].includes(carterCode)) {
      // Per N56 con carter 1/1I: Mantieni V1/V2 in C (Y ha già solo V)
      finishForC = validatedFinishC;
    } else if (normalizedProfile === '6' && carterCode === '1') {
      // Per N6/N6 STADIO con carter 1: Mantieni V1/V2 in C (Y ha già solo V)
      finishForC = validatedFinishC;
    } else if (normalizedProfile === '106' && carterCode === '1') {
      // Per N106 con carter 1: Mantieni V1/V2 in C (Y ha già solo V)
      finishForC = validatedFinishC;
    } else {
      // Per tutti gli altri casi, rimuovi il numero
      finishForC = 'V';
    }
  }

  finalSection += finishForC;

  // Add final section
  code += `-${finalSection}`;

  return code;
};

export const getValidGlassSizes = (profileType) => {
  const normalized = normalizeProfileType(profileType);
  const sizes = {
    '106': ['16', '20'],
    '116': ['16', '20'],
    '56': ['12', '16'],
    '6': ['20', '24'],
    '176': ['16', '20', '24'],
    '186': ['16', '20', '24'],
    '50': ['16'] // N50 always has glass size 16
  };
  return sizes[normalized] || [];
};

export const formatGlassDisplay = (size) => {
  const halfSize = parseInt(size) / 2;
  return `${halfSize} + ${halfSize}`;
};

export const getValidCarterCodes = (profileType) => {
  const normalized = normalizeProfileType(profileType);
  switch (normalized) {
    case '106':
    case '6':
      return ['1']; // Only one option
    case '56':
      return ['1', '1I']; // Two options
    case '176':
    case '186':
      return ['2', '1', '1I']; // Three options
    case '116':
      return ['1', '1S', '1C']; // Three specific options
    case '50':
      return []; // N50 has no carter
    default:
      return [];
  }
};

// Helper function to determine if profile needs two finish colors
export const needsTwoFinishColors = (profileType, carterCode) => {
  const normalized = normalizeProfileType(profileType);
  // For 116 with carter 1 or 1S, we need two colors if first finish is "grezza"
  return normalized === '116' && ['1', '1S'].includes(carterCode);
};

// Helper function to determine if finishes Y and C should be the same
export const shouldUseIdenticalFinishes = (profileType) => {
  const normalized = normalizeProfileType(profileType);
  return ['106', '56', '6'].includes(normalized);
};
